import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, apiPost, ApiError } from "@/lib/api";

type KycDbStatus = "approved" | "pending" | "rejected";
type KycUiStatus = KycDbStatus | "not_started";

type KycSummary = {
  kyc?: {
    id: number | string;
    status: KycDbStatus;
    document_type?: string | null;
    document_number?: string | null;
    document_expiry?: string | null;
    review_notes?: string | null;
  } | null;
  status: KycUiStatus;
};

export default function KycStatus() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<KycSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function load() {
    try {
      setLoading(true);
      setError(null);
      const res = await apiGet("/xapi/kyc/status");
      setSummary(res?.data ?? res);
    } catch (e) {
      const err = e as ApiError;
      setError(err.message || "Failed to load KYC status.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const status: KycUiStatus = summary?.status ?? "not_started";

  const onResubmit = async () => {
    try { await apiPost("/xapi/kyc/reset", {}); } catch {}
    navigate("/kyc/create");
  };

  return (
    <div className="px-4 md:px-6 pb-24 md:pb-8 pt-4 md:pt-6">
      <div className="section-box rounded-2xl border border-gray-200 bg-white p-4 md:p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">KYC</h1>
          <span
            className={`chip ${
              status === "approved"
                ? "bg-green-100 text-green-700"
                : status === "pending"
                ? "bg-yellow-100 text-yellow-700"
                : status === "rejected"
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {status === "not_started" ? "Not started" : status[0].toUpperCase() + status.slice(1)}
          </span>
        </div>

        {loading ? (
          <div className="mt-6 skeleton h-24 rounded-xl" />
        ) : error ? (
          <div className="mt-6 alert-danger rounded-xl p-4">{error}</div>
        ) : status === "not_started" ? (
          <div className="mt-6 rounded-2xl border border-gray-200 p-4 md:p-6 bg-gray-50">
            <p className="text-lg">You haven’t started KYC yet.</p>
            <div className="mt-4">
              {/* IMPORTANT: go to create page */}
              <Link to="/kyc/create" className="btn">Start KYC</Link>
            </div>
          </div>
        ) : status === "pending" ? (
          <div className="mt-6 rounded-2xl border border-gray-200 p-4 md:p-6 bg-gray-50">
            <p className="text-lg font-medium">Your KYC is under review.</p>
            <p className="text-gray-500">We’ll notify you when it’s approved.</p>
          </div>
        ) : status === "approved" ? (
          <div className="mt-6 rounded-2xl border border-green-200 p-4 md:p-6 bg-green-50">
            <p className="text-lg font-medium text-green-800">Your KYC is approved.</p>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-red-200 p-4 md:p-6 bg-red-50">
            <p className="text-lg font-semibold text-red-700">KYC was rejected.</p>
            <p className="text-red-700/80">{summary?.kyc?.review_notes || "Please try again with clear photos."}</p>
            <div className="mt-4">
              <button className="btn" onClick={onResubmit}>Resubmit KYC</button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <button className="btn btn-white" onClick={() => navigate("/profile")}>
          Back to Profile
        </button>
      </div>
    </div>
  );
}
